import React, { Fragment, useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Card, CardBody, Col, Form, FormGroup, Label, Nav, Row } from "reactstrap";
import * as yup from "yup";
import ModalFormSubmit from "../../../../share-components/form/modalFormSubmit";
import CommonDataTable from "../../../../share-components/table/commonDataTable";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
  addCourierPairRate,
  closeRateDialog,
  deleteCourierPairRate,
  getCourierLookup,
  getCourierPairRates,
  openRateEditDialog,
  openRateNewDialog,
  selectRateDialog,
  updateCourierPairRate,
} from "../../store/courierRateSlice";
import { COURIER_COST_PERMISSION, NEW_ORDERS_ONLY_NOTE, RateInfoNote, filterSelectStyles, rateField } from "./courierCostShared";

/**
 * CRUD tab for a carrier x <second key> rate pivot (no id column; rows keyed by the pair).
 * config: { resource, title, lookup, secondKey, secondNameKey, secondLabel, rateKey, rateLabel, note }
 */
function PairRateTab({ config }) {
  const { resource, title, lookup, secondKey, secondNameKey, secondLabel, rateKey, rateLabel, note } = config;
  const dispatch = useDispatch();
  const [deliveryPartnerId, setDeliveryPartnerId] = useState("");
  const [name, setName] = useState("");
  const [tableLoading, setTableLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const currentUser = useSelector(({ authReducer }) => authReducer.data.user);
  const rows = useSelector(({ partner }) => partner.courierRate.pairRates[resource]) || [];
  const secondOptions = useSelector(({ partner }) => partner.courierRate.lookups[lookup]);
  const deliveryPartners = useSelector(({ partner }) => partner.courierServiceCost.deliveryPartners);
  const dialog = useSelector(selectRateDialog(resource));
  const canManage = currentUser.permission.includes(COURIER_COST_PERMISSION);
  const isEdit = dialog.type === "edit";

  const defaultValues = { delivery_partner_id: "", [secondKey]: "", [rateKey]: "" };
  const schema = yup.object().shape({
    delivery_partner_id: yup.string().required("You must select a courier"),
    [secondKey]: yup.string().required(`You must select a ${secondLabel.toLowerCase()}`),
    [rateKey]: rateField(rateLabel),
  });

  const { handleSubmit, formState, reset, register, setValue, getValues, trigger, watch } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { errors } = formState;

  // Synthetic id so the data table can key the composite-key rows.
  const gridData = useMemo(() => rows.map((row) => ({ ...row, id: `${row.delivery_partner_id}-${row[secondKey]}` })), [rows, secondKey]);

  useEffect(() => {
    dispatch(getCourierLookup(lookup));
  }, [dispatch, lookup]);

  useEffect(() => {
    getData();
  }, [dispatch, deliveryPartnerId, name]);

  const getData = () => {
    setTableLoading(true);
    dispatch(getCourierPairRates({ resource, filters: { delivery_partner_id: deliveryPartnerId, name } })).then(() => setTableLoading(false));
  };

  const tableColumns = [
    { name: "Courier", selector: (row) => row.delivery_partner_name, sortable: true, left: true },
    { name: secondLabel, selector: (row) => row[secondNameKey], sortable: true, left: true },
    { name: rateLabel, selector: (row) => row[rateKey], sortable: true, right: true },
  ];

  const closeDialog = () => dispatch(closeRateDialog(resource));

  const onEdit = (row) => {
    dispatch(
      openRateEditDialog({
        key: resource,
        data: { delivery_partner_id: String(row.delivery_partner_id), [secondKey]: String(row[secondKey]), [rateKey]: row[rateKey] },
      })
    );
  };

  const onDelete = (row) => {
    dispatch(deleteCourierPairRate({ resource, deliveryPartnerId: row.delivery_partner_id, secondId: row[secondKey] })).then(() => getData());
  };

  const onSubmitForm = (dialogState, data) => {
    setButtonDisable(true);
    const action =
      dialogState.type === "new"
        ? addCourierPairRate({ resource, data })
        : updateCourierPairRate({ resource, deliveryPartnerId: data.delivery_partner_id, secondId: data[secondKey], data: { [rateKey]: data[rateKey] } });
    dispatch(action).then((res) => {
      setButtonDisable(false);
      getData();
      if (!res.error) closeDialog();
    });
  };

  const selectedOf = (options, value) => options.find((option) => String(option.id) === String(value)) || null;

  return (
    <Fragment>
      <div className="product-grid">
        <div className="feature-products">
          {canManage ? (
            <Card>
              <Row>
                <Col sm="6">
                  <Nav tabs className="border-tab"></Nav>
                </Col>
                <Col sm="6">
                  <div className="float-sm-right p-3">
                    <a className="btn btn-sm btn-primary" onClick={() => dispatch(openRateNewDialog(resource))}>
                      {" "}
                      <i className="fa fa-plus"></i> Add {title}
                    </a>
                  </div>
                </Col>
              </Row>
            </Card>
          ) : (
            ""
          )}
          <Row>
            <Col sm="6">
              <Select
                placeholder={<div>All Couriers</div>}
                className="select2-filter"
                isClearable
                styles={filterSelectStyles}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={selectedOf(deliveryPartners, deliveryPartnerId)}
                onChange={(e) => setDeliveryPartnerId(e ? e.id : "")}
                options={deliveryPartners}
                components={{ IndicatorSeparator: () => null }}
              />
            </Col>
            <Col sm="6">
              <Form>
                <FormGroup className="m-0">
                  <input onChange={(e) => setName(e.target.value)} type="text" className="form-control border" placeholder={`Search By ${secondLabel}`} />
                  <i className="fa fa-search"></i>
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </div>
      </div>
      <Card>
        <CardBody>
          <RateInfoNote>
            {note} {NEW_ORDERS_ONLY_NOTE}
          </RateInfoNote>
          {tableLoading ? (
            <div style={{ minHeight: 400 }}>
              <PreLoader />
            </div>
          ) : (
            <div className="table-responsive product-table mt-4">
              <CommonDataTable
                headerColumns={tableColumns}
                gridData={gridData}
                pagination
                onEdit={canManage ? onEdit : ""}
                onDelete={canManage ? onDelete : ""}
              />
            </div>
          )}
        </CardBody>
      </Card>

      <ModalFormSubmit
        defaultValues={defaultValues}
        schema={schema}
        onSubmitForm={onSubmitForm}
        reset={reset}
        formState={formState}
        handleSubmit={handleSubmit}
        title={title}
        selector="partner"
        dialog={selectRateDialog(resource)}
        closeDialog={closeDialog}
        setValue={setValue}
        getValues={getValues}
        buttonDisable={buttonDisable}
      >
        <Row>
          <Col md="6">
            <FormGroup>
              <Label className="col-form-label">Courier</Label>
              <input type="hidden" {...register("delivery_partner_id")} />
              <Select
                placeholder="Select courier"
                isDisabled={isEdit}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={selectedOf(deliveryPartners, watch("delivery_partner_id"))}
                onChange={(e) => {
                  setValue("delivery_partner_id", e ? String(e.id) : "");
                  trigger("delivery_partner_id");
                }}
                className={`${errors.delivery_partner_id ? "is-invalid" : ""}`}
                options={deliveryPartners}
              />
              <div className="invalid-feedback">{errors.delivery_partner_id?.message}</div>
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label className="col-form-label">{secondLabel}</Label>
              <input type="hidden" {...register(secondKey)} />
              <Select
                placeholder={`Select ${secondLabel.toLowerCase()}`}
                isDisabled={isEdit}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={selectedOf(secondOptions, watch(secondKey))}
                onChange={(e) => {
                  setValue(secondKey, e ? String(e.id) : "");
                  trigger(secondKey);
                }}
                className={`${errors[secondKey] ? "is-invalid" : ""}`}
                options={secondOptions}
              />
              <div className="invalid-feedback">{errors[secondKey]?.message}</div>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label className="col-form-label">{rateLabel} (£)</Label>
              <input type="number" step="0.01" min="0" {...register(rateKey)} className={`form-control ${errors[rateKey] ? "is-invalid" : ""}`} />
              <div className="invalid-feedback">{errors[rateKey]?.message}</div>
            </FormGroup>
          </Col>
        </Row>
        {isEdit ? <RateInfoNote>To move this rate to another courier or {secondLabel.toLowerCase()}, delete it and add a new one.</RateInfoNote> : ""}
      </ModalFormSubmit>
    </Fragment>
  );
}

export default PairRateTab;
